import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginSuccess } from '../state/authSlice';
import { setRole } from '../state/uiSlice';
import { decodeToken, isEmployeeToken, resolveSessionRole, setRoleForUser } from '../api/session';
import { DEFAULT_ROLE, PROFILES, ROUTES } from '../config/navigation';

/**
 * Take on a session without going anywhere: store the token and resolve the
 * seat, and return the seat the app should run as.
 *
 * Sign-up needs this halfway through — its firm step searches firms and creates
 * one, both authenticated calls, while the person is still on the sign-up
 * screen — so storing the session and navigating are two steps, not one.
 */
export function useAdoptSession() {
  const dispatch = useDispatch();
  return (auth, chosenRole) => {
    const userId = auth.userId;
    if (chosenRole && userId) setRoleForUser(userId, chosenRole);

    /* Whether this is KI staff is a property of the token, so it is read once
       here and kept on the session — the console gate and anything else that
       asks should never have to re-decode the JWT to find out. */
    const claims = decodeToken(auth.authToken) || {};
    const userType = claims.user_type ?? claims.userType ?? null;

    dispatch(loginSuccess({
      authToken: auth.authToken,
      refreshToken: auth.refreshToken,
      userId,
      emailVerified: auth.emailVerified,
      isEmployee: isEmployeeToken(auth.authToken),
      userType: Array.isArray(userType) ? userType.join(',') : userType,
    }));

    const { assigned } = resolveSessionRole({ token: auth.authToken, userId });
    const effective = assigned || chosenRole || DEFAULT_ROLE;
    dispatch(setRole(effective));
    return effective;
  };
}

/** Where a seat opens: its own home screen. */
export const homeOf = (seat) => ROUTES[PROFILES[seat]?.landing] || ROUTES[PROFILES[DEFAULT_ROLE].landing];

/**
 * Land a session: store the token, resolve the seat, open its home screen (or
 * `next`, when given). Shared so the login and signup forms cannot drift on it.
 *
 * `chosenRole` is what signup collected; a role claim on the token wins over
 * it, and sign-in passes null to take whatever the session resolves.
 */
export function useLandSession() {
  const adopt = useAdoptSession();
  const navigate = useNavigate();
  return (auth, chosenRole, next) => {
    const effective = adopt(auth, chosenRole);
    navigate(next || homeOf(effective), { replace: true });
  };
}
